import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { CropTableProps } from 'utils/types';
import useCroptimizerCrop from 'api/hooks/useCroptimizerCrop';
import { unMaskInput, unMaskInputValue } from 'utils/helpers';
import { FieldProps } from 'features/Croptimizer/types';
import { cn } from 'utils/cn';

import CropTableHeader from './CropTableHeader';
import { TotalRow } from './TotalRow';
import { TableRow } from './TableRow';

import { useGenerateCrop } from '../../../hooks/useGenerateCrop';

interface Props {
  tableData: CropTableProps[];
  setTableData: (
    tableDataOrUpdater:
    | CropTableProps[]
    | ((prev: CropTableProps[]) => CropTableProps[])
  ) => void;
  setEditCropModal: (value: CropTableProps) => void;
  disabled: boolean;
  isPDF?: boolean;
  object: FieldProps['object'];
}

export const pdfColumnWidth = {
  1: 'w-[10%]',
  2: 'w-[15%]',
  3: 'w-[25%]',
};

const CropTable: FC<Props> = ({
  tableData,
  setTableData,
  setEditCropModal,
  disabled,
  isPDF,
  object,
}) => {
  const { t } = useTranslation();
  const { data: crop } = useCroptimizerCrop();
  const { generateCrop } = useGenerateCrop();
  const onDuplicate = (id: number) => {
    const getData = tableData?.find((data) => data.id === id);
    const value = crop?.find((el) => el.Crops === getData?.name);

    if (value && getData) {
      const duplicatedCrop = generateCrop(
        value,
        object,
        undefined,
        getData,
        tableData.length,
      );
      setTableData((prev: any) => [...prev, duplicatedCrop]);
    }
  };

  const onDelete = (id: number) => {
    const deletedItemIndex = tableData?.findIndex((data) => data.id === id);

    if (deletedItemIndex !== -1) {
      setTableData((prevTableData) => (prevTableData as CropTableProps[])
        .filter((_, index) => index !== deletedItemIndex)
        .map((item, index) => {
          if (index >= deletedItemIndex) {
            const value = crop?.find((el) => el.Crops === item.name);

            return value
              ? generateCrop(value, object, item.id, item, index)
              : item;
          }

          return item;
        }),
      );
    }
  };

  const onEdit = (value: CropTableProps) => {
    setEditCropModal(value);
  };

  // Calculate totals
  const totals = useMemo(() => {
    const totalGainPrice =
      tableData?.reduce((sum, data) => sum + data.yieldPrice, 0) || 0;

    const totalN = unMaskInputValue(
      String(
        parseFloat(
          tableData
            ?.reduce((sum, data) => sum + Number(+unMaskInput(data.n)), 0)
            .toFixed(2) || '0',
        ),
      ),
    );

    const totalP = unMaskInputValue(
      String(
        parseFloat(
          tableData
            ?.reduce((sum, data) => sum + Number(+unMaskInput(data.p)), 0)
            .toFixed(2) || '0',
        ),
      ),
    );

    const totalK = unMaskInputValue(
      String(
        parseFloat(
          tableData
            ?.reduce((sum, data) => sum + Number(+unMaskInput(data.k)), 0)
            .toFixed(2) || '0',
        ),
      ),
    );

    const totalNPK =
      tableData?.reduce((sum, data) => sum + Math.round(data.npkGains), 0) || 0;
    const totalMonsp =
      tableData?.reduce((sum, data) => sum + data.monps, 0) || 0;
    const totalTotal =
      tableData?.reduce((sum, data) => sum + data.total, 0) || 0;

    return {
      totalGainPrice,
      totalN,
      totalP,
      totalK,
      totalNPK,
      totalMonsp,
      totalTotal,
    };
  }, [tableData]);

  return (
    <div className={cn('w-full', { 'overflow-scroll': !isPDF })}>
      <div className={cn('bg-[#FAFAFA] mt-14', { 'w-max mx-auto': !isPDF })}>
        <CropTableHeader pdfColumnWidth={pdfColumnWidth} isPDF={isPDF} />

        {tableData?.map((data) => (
          <TableRow
            key={data.id}
            data={data}
            isPDF={isPDF}
            disabled={disabled}
            tableDataLength={tableData.length}
            onDuplicate={onDuplicate}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}

        <TotalRow totals={totals} isPDF={isPDF} />
      </div>

      {/* Tooltips */}
      {!isPDF && (
        <>
          <Tooltip
            id="edit"
            place="bottom-start"
            className="tooltip"
            variant="light"
            noArrow
          >
            {t('modify')}
          </Tooltip>
          <Tooltip
            id="duplicate"
            place="bottom-start"
            className="tooltip"
            variant="light"
            noArrow
          >
            {t('duplicate')}
          </Tooltip>
          <Tooltip
            id="delete"
            place="bottom-start"
            className="tooltip"
            variant="light"
            noArrow
          >
            {t('delete')}
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default CropTable;
